import Logger from '../logger';

const logger = new Logger('AudioUtils');

// Create a global audio element for playback
let globalAudioElement = null;

// Helper function to play audio reliably
export const playAudio = (audioElement, stream) => {
	if (!audioElement || !stream) return Promise.reject(new Error('Missing audio element or stream'));

	// Always set the stream source
	audioElement.srcObject = stream;

	return audioElement.play()
		.then(() => {
			logger.debug('Audio playback started successfully');
			return true;
		})
		.catch(error => {
			logger.error('Audio playback failed:', error);

			// Create one-time click handler to retry playback
			const retryPlay = () => {
				audioElement.play()
					.then(() => logger.debug('Audio playback started after user interaction'))
					.catch(e => logger.error('Still failed to play audio:', e));

				document.removeEventListener('click', retryPlay);
			};

			document.addEventListener('click', retryPlay);
			return true;
		});
};

// Initialize the audio context and element
export const initAudio = () => {
	// Create audio element if it doesn't exist
	if (!globalAudioElement) {
		globalAudioElement = document.createElement('audio');
		globalAudioElement.id = 'jssip-audio-element';
		globalAudioElement.autoplay = true;
		globalAudioElement.setAttribute('playsinline', '');
		globalAudioElement.onerror = (e) => logger.error('Audio element error:', e);
		globalAudioElement.volume = 1.0;

		document.body.appendChild(globalAudioElement);
		logger.debug('Created global audio element');

		// Initialize AudioContext
		try {
			const AudioContext = window.AudioContext || window.webkitAudioContext;
			const audioCtx = new AudioContext();

			if (audioCtx.state === 'suspended') {
				document.addEventListener('click', function resumeAudio() {
					audioCtx.resume().then(() => {
						logger.debug('AudioContext resumed');
						document.removeEventListener('click', resumeAudio);
					});
				});
			}
		} catch (err) {
			logger.error('AudioContext initialization error:', err);
		}

		document.addEventListener('click', initAudioAfterUserInteraction, { once: true });
		setAudioOutputDevice();
	}

	return globalAudioElement;
};

// Set audio output device (for Chrome)
export const setAudioOutputDevice = async (deviceId = 'default') => {
	if (!globalAudioElement) return;

	// Check if setSinkId is supported
	if (typeof globalAudioElement.setSinkId === 'function') {
		try {
			// Get available audio output devices
			const devices = await navigator.mediaDevices.enumerateDevices();
			const audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');

			console.log('Available audio output devices:', audioOutputDevices);

			// Use the specified deviceId or the default device
			await globalAudioElement.setSinkId(deviceId);
			console.log(`Audio output device set to: ${deviceId}`);
		} catch (err) {
			console.error('Error setting audio output device:', err);
		}
	} else {
		console.log('setSinkId not supported on this browser');
	}
};

// Initialize audio after user interaction
const initAudioAfterUserInteraction = () => {
	// Remove existing event listener if it exists
	document.removeEventListener('click', initAudioAfterUserInteraction);

	// Create AudioContext now that we have user interaction
	try {
		const AudioContext = window.AudioContext || window.webkitAudioContext;
		if (AudioContext) {
			const audioContext = new AudioContext();
			console.log('AudioContext created after user interaction:', audioContext.state);
		}
	} catch (e) {
		console.error('Error initializing AudioContext:', e);
	}
};

// Get the global audio element
export const getGlobalAudioElement = () => globalAudioElement;


